import { useMemo, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowUp } from '@fortawesome/pro-solid-svg-icons/faCircleArrowUp';
import { faCircleArrowDown } from '@fortawesome/pro-solid-svg-icons/faCircleArrowDown';
import { _js } from '@ifixit/localize';
import { color } from '@ifixit/primitives';
import { Card } from '@ifixit/ui/components/card';
import { Table } from '@ifixit/ui/components/table';
import type { TableCell } from '@ifixit/ui/components/table';

type VoteDataType = {
   userProfileLink: string;
   userid: string;
   username: string;
   up: number;
   upPercent: number;
   down: number;
   downPercent: number;
};

type ReputationPointsDataType = {
   today: number;
   week: number;
   month: number;
   year: number;
   all: number;
};

export type VoteHistoryDataType = {
   userid?: number;
   username?: string;
   isCurrentUser?: boolean;
   reputationPoints?: ReputationPointsDataType;
   votesByUser?: VoteDataType[];
   votesByOthers?: VoteDataType[];
   upVotes?: number;
   downVotes?: number;
   usersUrl?: string;
};

export const VoteHistory = memo(
   ({
      userid,
      username = '',
      isCurrentUser = false,
      reputationPoints,
      votesByUser,
      votesByOthers,
      upVotes = 0,
      downVotes = 0,
      usersUrl,
   }: VoteHistoryDataType) => {
      const showVotes = upVotes > 0 || downVotes > 0 || !!votesByUser || !!votesByOthers;
      const showHistory = !!votesByUser && !!votesByOthers;

      return (
         <div className="flex flex-col gap-4 w-full">
            <ReputationPoints reputationPoints={reputationPoints} usersUrl={usersUrl} />
            {showVotes && <Votes upVotes={upVotes} downVotes={downVotes} />}
            {showHistory && (
               <History
                  userid={userid}
                  username={username}
                  isCurrentUser={isCurrentUser}
                  votesByUser={votesByUser}
                  votesByOthers={votesByOthers}
               />
            )}
         </div>
      );
   }
);

VoteHistory.displayName = 'VoteHistory';

// Memoized reputation points component
const ReputationPoints = memo(
   ({
      reputationPoints,
      usersUrl,
   }: { reputationPoints?: ReputationPointsDataType; usersUrl?: string }) => {
      const { all, month, week, today } = reputationPoints ?? {};

      // Memoize periods calculation to avoid recreation on every render
      const periods = useMemo(
         () =>
            [
               { key: 'all', label: _js('All Time'), points: all ?? 0 },
               { key: 'month', label: _js('Month'), points: month ?? 0 },
               { key: 'week', label: _js('Week'), points: week ?? 0 },
               { key: 'today', label: _js('Today'), points: today ?? 0 },
            ] as const,
         [all, month, week, today]
      );

      return (
         <div className="flex flex-col gap-4 w-full">
            <h3 className="heading-24">{_js('Reputation Points')}</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
               {periods.map(({ key, label, points }) => (
                  <Card className="flex flex-1 justify-start" key={key}>
                     <div className="whitespace-nowrap">
                        <div className="text-sm font-normal">{label}</div>
                        <div className="text-xl font-medium">{points.toLocaleString()}</div>
                     </div>
                  </Card>
               ))}
            </div>
            <a href={usersUrl}>{_js('View Leaderboard')}</a>
         </div>
      );
   }
);

ReputationPoints.displayName = 'ReputationPoints';

// Memoized votes component
const Votes = memo(({ upVotes, downVotes }: { upVotes: number; downVotes: number }) => {
   // Memoize formatted vote counts to avoid repeated toLocaleString calls
   const formattedUpVotes = useMemo(() => upVotes.toLocaleString(), [upVotes]);
   const formattedDownVotes = useMemo(() => downVotes.toLocaleString(), [downVotes]);

   return (
      <>
         <h3 className="mb-0 text-2xl font-semibold text-black">{_js('Votes')}</h3>
         <div className="flex align-center gap-3 w-full">
            <Card title={_js('Up Votes')} className="flex flex-1 items-center gap-1.5">
               <FontAwesomeIcon icon={faCircleArrowUp} color={color.lime[500]} />
               <span className="text-xl font-medium">{formattedUpVotes}</span>
            </Card>
            <Card title={_js('Down Votes')} className="flex flex-1 items-center gap-1.5">
               <FontAwesomeIcon icon={faCircleArrowDown} color={color.red[500]} />
               <span className="text-xl font-medium">{formattedDownVotes}</span>
            </Card>
         </div>
      </>
   );
});

Votes.displayName = 'Votes';

// Memoized history component
const History = memo(
   ({
      username,
      userid,
      isCurrentUser,
      votesByUser,
      votesByOthers,
   }: {
      username: string;
      userid?: number;
      isCurrentUser: boolean;
      votesByUser: VoteDataType[];
      votesByOthers: VoteDataType[];
   }) => {
      /* Translators: %1 -> account username */
      const votesByUserString = isCurrentUser ? _js('Votes by you') : _js('Votes by %1', username);
      const votesByOthersString = _js('Votes by others');

      return (
         <>
            <VoteItem userid={userid} votesBy={votesByUserString} votes={votesByUser} />
            <VoteItem userid={userid} votesBy={votesByOthersString} votes={votesByOthers} />
         </>
      );
   }
);

History.displayName = 'History';

// Memoized vote item component
const VoteItem = memo(
   ({
      userid,
      votesBy,
      votes,
   }: {
      userid?: number;
      votesBy: string;
      votes: VoteDataType[];
   }) => {
      const hasVotes = votes.length > 0;

      return (
         <>
            <h3 className="mb-0 text-2xl font-semibold text-black">{votesBy}</h3>
            <div className="p-0">
               {hasVotes ? (
                  <VoteTable userid={userid} voteData={votes} votesBy={votesBy} />
               ) : (
                  <p>{_js('No Votes')}</p>
               )}
            </div>
         </>
      );
   }
);

VoteItem.displayName = 'VoteItem';

// Vote table column definitions - extracted as constant for performance
const VOTE_TABLE_CELLS: TableCell[] = [
   { id: 'username', label: _js('Name'), cellWidth: 'w-[50%]' },
   {
      id: 'up',
      label: <FontAwesomeIcon icon={faCircleArrowUp} color={color.lime[500]} />,
      cellStyles: 'text-right!',
   },
   {
      id: 'upPercent',
      label: '%',
      cellStyles: 'text-right!',
   },
   {
      id: 'down',
      label: <FontAwesomeIcon icon={faCircleArrowDown} color={color.red[500]} />,
      cellStyles: 'text-right!',
   },
   {
      id: 'downPercent',
      label: '%',
      cellStyles: 'text-right!',
   },
];

// Memoized vote table component
const VoteTable = memo(
   ({
      userid,
      voteData,
      votesBy,
   }: {
      userid?: number;
      voteData: VoteDataType[];
      votesBy: string;
   }) => {
      // Memoize vote rows transformation to avoid recalculation on every render
      const voteRows = useMemo(
         () =>
            voteData.map(line => ({
               id: `${line.userid}`,
               username: line.username,
               up: line.up,
               upPercent: `${line.upPercent}%`,
               down: line.down,
               downPercent: `${line.downPercent}%`,
            })),
         [voteData]
      );

      // Memoize viewAllUrl to avoid string concatenation on every render
      const viewAllUrl = useMemo(() => (userid ? `/users/${userid}/votes` : undefined), [userid]);

      return (
         <Table
            data={voteRows}
            cells={VOTE_TABLE_CELLS}
            maxRows={6}
            viewAllUrl={viewAllUrl}
            viewAllText={votesBy}
         />
      );
   }
);

VoteTable.displayName = 'VoteTable';
